import {
  type ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useTranslation} from 'react-i18next'

import {HiddenFileInput} from './ServicesModal.styled'

import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Input} from '~/components/general/input/Input'
import {Button} from '~/components/general/button/Button'
import FilteredSelect from '~/components/general/select/FilteredSelect'
import {SideModal} from '~/components/general/modal/SideModal'
import {
  useCreatePartnerService,
  useGetPartnerServices,
  useUpdatePartnerService,
  useGetQuestionnairesShort,
  type PartnerServiceUpsertRequestBody,
} from '~/api/partnerAdminServices'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {TextArea} from '~/components/general/textArea/TextArea'

type ServicesModalProps = {
  isVisible: boolean
  onClose: () => void
  serviceId: string | null
}

export const ServicesModal = ({
  isVisible,
  onClose,
  serviceId,
}: ServicesModalProps) => {
  const {t} = useTranslation<string>()
  const {addNotification} = useContext(NotificationContext)
  const isEditing = !!serviceId
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {data: services} = useGetPartnerServices()
  const editingService = useMemo(
    () => services?.find((s) => s.id === serviceId) || null,
    [services, serviceId],
  )

  const [name, setName] = useState('')
  const [internalCode, setInternalCode] = useState('')
  const [duration, setDuration] = useState('30')
  const [preQuestionnaireId, setPreQuestionnaireId] = useState<
    string | undefined
  >(undefined)
  const [postQuestionnaireId, setPostQuestionnaireId] = useState<
    string | undefined
  >(undefined)
  const [location, setLocation] = useState('')
  const [contacts, setContacts] = useState('')
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const [removeAttachmentOnSave, setRemoveAttachmentOnSave] = useState(false)

  const {data: questionnaires} = useGetQuestionnairesShort('')
  const questionnaireOptions = useMemo(
    () =>
      (questionnaires || []).map((q) => ({
        label: q.title,
        value: q.id,
      })),
    [questionnaires],
  )

  const optionsWithNone = useMemo(
    () => [{label: t('None'), value: ''}, ...questionnaireOptions],
    [questionnaireOptions, t],
  )

  useEffect(() => {
    if (!isVisible) return
    setAttachmentFile(null)
    setRemoveAttachmentOnSave(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [isVisible, serviceId])

  useEffect(() => {
    if (!isVisible) return
    if (editingService) {
      setName(editingService.name || '')
      setInternalCode(editingService.internalCode || '')
      setDuration(editingService.appointmentDuration?.toString() || '')
      setPreQuestionnaireId(editingService.preQuestionnaireId || undefined)
      setPostQuestionnaireId(editingService.postQuestionnaireId || undefined)
      setLocation(editingService.location || '')
      setContacts(editingService.contacts || '')
    } else {
      setName('')
      setInternalCode('')
      setDuration('30')
      setPostQuestionnaireId(undefined)
      setLocation('')
      setContacts('')
      if (questionnaireOptions.length > 0) {
        setPreQuestionnaireId(questionnaireOptions[0].value)
      } else {
        setPreQuestionnaireId(undefined)
      }
    }
  }, [isVisible, editingService, questionnaireOptions])

  useEffect(() => {
    if (!isVisible || isEditing) return
    if (questionnaireOptions.length > 0 && !preQuestionnaireId) {
      setPreQuestionnaireId(questionnaireOptions[0].value)
    }
  }, [isVisible, isEditing, questionnaireOptions, preQuestionnaireId])

  const {mutate: createService, isPending: isCreating} =
    useCreatePartnerService()
  const {mutate: updateService, isPending: isUpdating} =
    useUpdatePartnerService(serviceId || '')

  const canSave =
    name.trim().length > 0 &&
    internalCode.trim().length > 0 &&
    Number(duration) > 0

  const handleAttachmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setAttachmentFile(file)
      setRemoveAttachmentOnSave(false)
    }
  }

  const handleClearAttachmentSelection = () => {
    setAttachmentFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSave = () => {
    if (!canSave) return

    const attachmentForUpdate: Pick<
      PartnerServiceUpsertRequestBody,
      'attachmentFileId'
    > = {}
    if (isEditing && serviceId) {
      if (attachmentFile) {
        // Replacing attachment: omit attachmentFileId so the server treats it as null with a file part.
      } else if (removeAttachmentOnSave) {
        attachmentForUpdate.attachmentFileId = null
      } else if (editingService?.attachmentFileId) {
        attachmentForUpdate.attachmentFileId = editingService.attachmentFileId
      }
    }

    const payload: PartnerServiceUpsertRequestBody = {
      name: name.trim(),
      internalCode: internalCode.trim(),
      appointmentDuration: Number(duration),
      preQuestionnaireId: preQuestionnaireId || undefined,
      postQuestionnaireId: postQuestionnaireId || undefined,
      location: location || undefined,
      contacts: contacts || undefined,
      ...attachmentForUpdate,
    }

    const onSuccess = () => onClose()
    const onError = (error: Error) =>
      addNotification(error.message, NotificationType.Error)

    const multipartPayload = {
      body: payload,
      ...(attachmentFile ? {file: attachmentFile} : {}),
    }

    if (isEditing && serviceId) {
      updateService(multipartPayload, {onSuccess, onError})
    } else {
      createService(multipartPayload, {onSuccess, onError})
    }
  }

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      header={
        <Container>
          <Typography size="lg" weight="medium">
            {isEditing ? t('Edit service') : t('Add a service')}
          </Typography>
        </Container>
      }>
      <Container flex direction="column" spacing={3}>
        <Container flex direction="column" spacing={1}>
          <Typography>{t('Name')}</Typography>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </Container>

        <Container flex direction="column" spacing={1}>
          <Typography>{t('Code')}</Typography>
          <Input
            value={internalCode}
            onChange={(e) => setInternalCode(e.target.value)}
            required
          />
        </Container>

        <Container flex direction="column" spacing={1}>
          <Typography>{t('Duration (minutes)')}</Typography>
          <Input
            type="number"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            required
          />
        </Container>

        <Container flex direction="column" spacing={1}>
          <Typography>{t('Pre-questionnaire')}</Typography>
          <FilteredSelect
            key={preQuestionnaireId || 'none'}
            width={300}
            placeholder={t('Select a questionnaire')}
            options={optionsWithNone}
            initialValue={
              questionnaireOptions.find((o) => o.value === preQuestionnaireId)
                ?.label || ''
            }
            onChange={(value: string | undefined) =>
              setPreQuestionnaireId(value || undefined)
            }
          />
        </Container>

        <Container flex direction="column" spacing={1}>
          <Typography>{t('Location')}</Typography>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </Container>

        <Container flex direction="column" spacing={1}>
          <Typography>{t('Contacts')}</Typography>
          <TextArea
            value={contacts}
            onChange={(e) => setContacts(e.target.value)}
            rows={5}
            height={null}
          />
        </Container>

        <Container flex direction="column" spacing={1}>
          <Typography>{t('Service attachment')}</Typography>
          {editingService?.attachmentFileId && !removeAttachmentOnSave ? (
            <Typography size="sm">
              {t('Service attachment replace hint')}
            </Typography>
          ) : null}
          {editingService?.attachmentFileId && removeAttachmentOnSave ? (
            <Typography size="sm">
              {t('The attachment will be removed when you save.')}
            </Typography>
          ) : null}
          <Container flex align="center" spacing={2} wrap>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              onChange={handleAttachmentChange}
              aria-label={t('Choose file')}
            />
            <Button
              color="secondary"
              onClick={() => fileInputRef.current?.click()}>
              {t('Choose file')}
            </Button>
            {editingService?.attachmentFileId &&
            !attachmentFile &&
            !removeAttachmentOnSave ? (
              <Button
                color="secondary"
                onClick={() => setRemoveAttachmentOnSave(true)}>
                {t('Remove attachment')}
              </Button>
            ) : null}
            {editingService?.attachmentFileId && removeAttachmentOnSave ? (
              <Button
                color="secondary"
                onClick={() => setRemoveAttachmentOnSave(false)}>
                {t('Keep attachment')}
              </Button>
            ) : null}
            {attachmentFile ? (
              <>
                <Typography size="sm">{attachmentFile.name}</Typography>
                <Button
                  color="secondary"
                  onClick={handleClearAttachmentSelection}>
                  {t('Clear file selection')}
                </Button>
              </>
            ) : null}
          </Container>
        </Container>

        <Container flex justify="flex-end" spacing={2}>
          <Button color="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave || isCreating || isUpdating}>
            {t('Save')}
          </Button>
        </Container>
      </Container>
    </SideModal>
  )
}
